import { Card, CardContent, Separator, cn } from '@mlm/ui';
import type { KpiCardData } from '../../lib/api';
import { Sparkline } from './sparkline';
import { DeltaChip } from './delta-chip';
import { TenantWideBadge } from './tenant-wide-badge';
import { commissionRate, formatPeso, retainedAfter, scopesComparable } from './format';

/**
 * The month's money, read like a ledger line rather than a wall of equal
 * tiles: sales in, commissions out, and the plain-language relationship
 * between them. This is the dashboard's primary block because the product's
 * whole job is making money legible.
 *
 * Under a branch filter the two halves stop being comparable — sales narrow,
 * commissions can't — so the relationship line is withheld rather than computed
 * across scopes. See `scopesComparable` for why that isn't a cosmetic call.
 */
export function MoneySummary({
  sales,
  commissions,
  filtered = false,
}: {
  sales?: KpiCardData;
  commissions?: KpiCardData;
  filtered?: boolean;
}) {
  const comparable = scopesComparable(sales, commissions, filtered);
  const rate =
    comparable && sales && commissions ? commissionRate(sales.value, commissions.value) : null;
  const retained =
    comparable && sales && commissions
      ? formatPeso(retainedAfter(sales.value, commissions.value))
      : null;

  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-caption uppercase tracking-[0.1em] font-medium text-muted-foreground">
          This month
        </p>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Figure
            label="Sales"
            value={sales ? formatPeso(sales.value) : '0.00'}
            delta={sales?.deltaPct ?? null}
            sparkline={sales?.sparkline ?? []}
            stroke="hsl(var(--foreground))"
            badge={<TenantWideBadge show={filtered && sales?.scope === 'tenant'} />}
          />
          <div className="sm:border-l sm:border-border sm:pl-6">
            <Figure
              label="Commissions paid"
              value={commissions ? `−${formatPeso(commissions.value)}` : '0.00'}
              delta={commissions?.deltaPct ?? null}
              sparkline={commissions?.sparkline ?? []}
              tone="debit"
              stroke="hsl(var(--debit))"
              badge={<TenantWideBadge show={filtered && commissions?.scope === 'tenant'} />}
            />
          </div>
        </div>

        {rate && retained ? (
          <>
            <Separator className="my-4" />
            <p className="text-body text-muted-foreground">
              <span className="font-medium tabular-nums text-foreground">{rate}</span>{' '}
              of sales went to commissions
              {' · '}
              <span className="font-medium tabular-nums text-credit">{retained}</span>{' '}
              retained
            </p>
          </>
        ) : filtered && sales && commissions ? (
          <>
            <Separator className="my-4" />
            {/* Deliberately withheld, not hidden: dividing one branch's sales by
                every branch's commissions reads "312% went to commissions" and a
                negative "retained" in credit green. Both figures above are still
                on screen with their own scope labels. */}
            <p className="text-body text-muted-foreground">
              Commission rate and retained are tenant-wide — clear the branch filter to see
              them.
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Figure({
  label,
  value,
  delta,
  sparkline,
  tone,
  stroke,
  badge,
}: {
  label: string;
  value: string;
  delta: string | null;
  sparkline: number[];
  tone?: 'debit';
  stroke: string;
  badge?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <p className="text-caption uppercase tracking-[0.1em] font-medium text-muted-foreground">
            {label}
          </p>
          {badge}
        </div>
        <DeltaChip deltaPct={delta} />
      </div>
      <p
        className={cn(
          'mt-1 text-kpi tabular-nums',
          tone === 'debit' ? 'text-debit' : 'text-foreground',
        )}
      >
        {value}
      </p>
      {sparkline.length > 0 ? (
        <Sparkline values={sparkline} stroke={stroke} className="mt-3 h-8 w-full" />
      ) : null}
    </div>
  );
}
